import React, { useState, useRef, ChangeEvent } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  ArrowLeft,
  ImagePlus,
  Star,
  Trash2,
  Upload,
  UserRound,
} from "lucide-react";
import { createFoodReview } from "../../../../../Redux/products review/slice";
import { reviewType } from "../../../../../Redux/products review/types";
import { uploadToCloudinary } from "../../../../../utility/uploadToCloudinary";
import { AppDispatch, RootState } from "../../../../../store";
import "./FoodProductFeedback.css";

const FoodProductFeedback = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { isCreating } = useSelector(
    (state: RootState) => state.productsReview,
  );

  const [rating, setRating] = useState<number>(0);
  const [hoverRating, setHoverRating] = useState<number>(0);
  const [description, setDescription] = useState<string>("");
  const [title, setTitle] = useState<string>("");
  const [publicName, setPublicName] = useState<string>("");

  const [mediaFiles, setMediaFiles] = useState<
    { file: File; type: "image" | "video"; preview: string }[]
  >([]);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const activeRating = hoverRating || rating;
  const goBack = () => navigate("/FoodOrderList");

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFiles = Array.from(e.target.files);
      const newMedia = selectedFiles.map((file) => {
        const isVideo = file.type.startsWith("video/");
        return {
          file,
          type: (isVideo ? "video" : "image") as "image" | "video",
          preview: URL.createObjectURL(file),
        };
      });
      setMediaFiles((prev) => [...prev, ...newMedia]);
    }
    // Clear input so same file can be selected again if needed
    if (e.target) {
      e.target.value = "";
    }
  };

  const removeMedia = (index: number) => {
    setMediaFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!id) {
      toast.error("Product ID is missing.");
      return;
    }
    if (rating === 0) {
      toast.error("Please provide a rating.");
      return;
    }
    if (!title.trim()) {
      toast.error("Please provide a title for your review.");
      return;
    }
    if (!publicName.trim()) {
      toast.error("Please provide your public name.");
      return;
    }

    try {
      setIsUploading(true);
      const images: string[] = [];
      const videos: string[] = [];

      for (const media of mediaFiles) {
        const url = await uploadToCloudinary(media.file);
        if (media.type === "image") {
          images.push(url);
        } else {
          videos.push(url);
        }
      }

      const fullDescription = `${title}\n\n${description}\n\n- By ${publicName}`;

      const reviewData = {
        rating,
        description: fullDescription,
        productId: id,
        images,
        videos,
        reviewType: reviewType.FOOD,
      };

      const resultAction = await dispatch(createFoodReview(reviewData));

      setIsUploading(false);

      if (createFoodReview.fulfilled.match(resultAction)) {
        toast.success("Review submitted successfully!");
        setRating(0);
        setDescription("");
        setTitle("");
        setPublicName("");
        setMediaFiles([]);
        navigate(-1);
      } else {
        toast.error(
          (resultAction.payload as string) || "Failed to submit review",
        );
      }
    } catch (error: any) {
      setIsUploading(false);
      toast.error(error.message || "An error occurred while uploading media");
    }
  };

  return (
    <section className="food-review-page">
      <div className="food-review-shell">
        <button
          type="button"
          className="food-review-back"
          onClick={goBack}
        >
          <ArrowLeft size={18} strokeWidth={2.1} />
          <span>Back to Food Orders</span>
        </button>

        <header className="food-review-header">
          <div className="food-review-heading">
            <span className="food-review-heading-icon">
              <Star size={22} strokeWidth={2} />
            </span>
            <div>
              <span className="food-review-kicker">VERIFIED FOOD ORDER</span>
              <h1>Write a Food Review</h1>
              <p>
                Share what you liked and what other customers should know
                before ordering.
              </p>
            </div>
          </div>
          {id && (
            <div className="food-review-order-chip">
              <span>PRODUCT ID</span>
              <strong>#{id}</strong>
            </div>
          )}
        </header>

        <section className="food-review-product-card">
          <div className="food-review-product-image">
            <img
              src="../../assets/img/food/food-6.jpg"
              alt="Food item"
              loading="lazy"
            />
          </div>
          <div className="food-review-product-copy">
            <span>HOW WAS THE ITEM?</span>
            <h2>Review Food Product</h2>
            <p>
              Ordered from <strong>your recent food order</strong>
            </p>
            <div className="food-review-product-meta">
              <span>Verified Order</span>
            </div>
          </div>
        </section>

        <form
          className="food-review-form"
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <section className="food-review-section">
            <div className="food-review-section-head">
              <div>
                <span className="food-review-section-label">
                  YOUR RATING
                </span>
                <h2>Rate this item</h2>
                <p>
                  Select the rating that best matches your overall food
                  experience.
                </p>
              </div>
              <span className="food-review-step">01</span>
            </div>
            <div
              className="food-review-stars"
              onMouseLeave={() => setHoverRating(0)}
            >
              {[1, 2, 3, 4, 5].map((value) => (
                <button
                  type="button"
                  key={value}
                  className={value <= activeRating ? "active" : ""}
                  onMouseEnter={() => setHoverRating(value)}
                  onClick={() => setRating(value)}
                  aria-label={`Rate ${value} star`}
                  aria-pressed={rating === value}
                >
                  <Star size={31} strokeWidth={1.8} />
                </button>
              ))}
            </div>
          </section>

          <section className="food-review-section">
            <div className="food-review-section-head">
              <div>
                <span className="food-review-section-label">REVIEW</span>
                <h2>Write a review</h2>
                <p>
                  Tell customers what stood out about taste, quality,
                  portion, freshness and overall experience.
                </p>
              </div>
              <span className="food-review-step">02</span>
            </div>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={1200}
              placeholder="What should other customers know about this item?"
              aria-label="Food review"
            />
            <div className="food-review-character-count">
              {description.length} /1200
            </div>
          </section>

          <section className="food-review-section">
            <div className="food-review-section-head">
              <div>
                <span className="food-review-section-label">MEDIA</span>
                <h2>Share photos or a video</h2>
                <p>Add images or a video of your food.</p>
              </div>
              <span className="food-review-step">03</span>
            </div>

            <div
              className="food-review-dropzone"
              onClick={() => fileInputRef.current?.click()}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  fileInputRef.current?.click();
                }
              }}
            >
              <span className="food-review-drop-icon">
                <Upload size={24} strokeWidth={1.9} />
              </span>
              <div>
                <strong>Choose photos or a video</strong>
                <span>Select files from your device</span>
              </div>
              <div className="food-review-upload-buttons">
                <button type="button">
                  <ImagePlus size={17} strokeWidth={2} />
                  Add Media
                </button>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*"
                multiple
                hidden
                onChange={handleFileChange}
              />
            </div>

            {mediaFiles.length > 0 && (
              <div className="food-review-media-grid">
                {mediaFiles.map((media, index) => (
                  <article
                    className={`food-review-media-card ${
                      media.type === "video" ? "video" : ""
                    }`}
                    key={index}
                  >
                    {media.type === "image" ? (
                      <img src={media.preview} alt="preview" />
                    ) : (
                      <video src={media.preview} muted playsInline />
                    )}
                    <button
                      type="button"
                      className="food-review-media-remove"
                      onClick={() => removeMedia(index)}
                      aria-label="Remove media"
                    >
                      <Trash2 size={15} strokeWidth={2} />
                    </button>
                  </article>
                ))}
              </div>
            )}
          </section>

          <section className="food-review-section">
            <div className="food-review-section-head">
              <div>
                <span className="food-review-section-label">
                  REVIEW DETAILS
                </span>
                <h2>Finish your review</h2>
                <p>
                  Add a clear title and the public name shown with your
                  review.
                </p>
              </div>
              <span className="food-review-step">04</span>
            </div>
            <div className="food-review-field-grid">
              <label>
                <span>
                  Title your review<b>*</b>
                </span>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={120}
                  placeholder="What's most important to know?"
                />
              </label>
              <label>
                <span>
                  What&apos;s your public name?<b>*</b>
                </span>
                <div className="food-review-name-input">
                  <UserRound size={17} strokeWidth={2} />
                  <input
                    type="text"
                    value={publicName}
                    onChange={(e) => setPublicName(e.target.value)}
                    maxLength={60}
                    placeholder="Your public name"
                  />
                </div>
              </label>
            </div>
          </section>

          <footer className="food-review-form-footer">
            <button
              type="button"
              className="secondary"
              onClick={goBack}
              disabled={isCreating || isUploading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="primary"
              disabled={isCreating || isUploading}
            >
              {isCreating || isUploading
                ? "Submitting..."
                : "Submit Review"}
            </button>
          </footer>
        </form>
      </div>
    </section>
  );
};

export default FoodProductFeedback;
